<script setup lang="ts">
import { Checkbox } from '@/components/ui/checkbox'

const props = defineProps({
	filterInstance: {
		type: Object,
		required: true,
	},
	filterContent: {
		type: Object,
		required: true,
	},
	locationId: {
		type: String,
		default: 'MercariSearch',
	},
})

const {
	filterState,
	hasPriceError,
	isMinPriceValid,
	isMaxPriceValid,
	isPriceRangeValid,
	handleCategoryChange,
	handleFilterChange,
	handlePriceChange,
	clearFiltersOnly,
	categoryLevels,
} = props.filterInstance

const handleCheckboxChange = (type: string, id: string, checked: boolean) => {
	const event = { target: { checked } }
	handleFilterChange(type, id, event)
}

const handleClear = () => {
	clearFiltersOnly()
}
</script>

<template>
	<div class="text-n-950">
		<div
			class="mb-24px flex items-center justify-between border-b border-n-200 py-8px"
		>
			<h3 class="typography-h5-str">{{ filterContent.title }}</h3>
			<button
				@click="handleClear"
				class="typography-b3-reg cursor-pointer text-o-500 hover:underline"
			>
				{{ filterContent.buttons.clear }}
			</button>
		</div>

		<div class="space-y-24px divide-y divide-n-200">
			<div>
				<h4 class="typography-b3-reg mb-16px">
					{{ filterContent.category.title }}
				</h4>
				<div class="space-y-12px">
					<div
						v-for="(level, index) in categoryLevels"
						:key="`category-level-${index}`"
					>
						<select
							:value="level.selectedId"
							@change="e => handleCategoryChange(e, index)"
							class="typography-s1-reg mb-12px w-full cursor-pointer rounded-lg border border-n-200 bg-white p-12px text-n-950 outline-none hover:border-o-200 focus:border-o-400 focus:ring-1 focus:ring-o-200"
						>
							<option
								v-for="option in level.options"
								:key="`${index}-${option.id}`"
								:value="option.id"
							>
								{{ option.label }}
							</option>
						</select>
					</div>
				</div>
			</div>

			<div>
				<h4 class="typography-b3-reg mb-16px">
					{{ filterContent.itemCondition.title }}
				</h4>
				<div class="flex flex-col gap-16px">
					<label
						v-for="option in filterContent.itemCondition.options"
						:key="option.id"
						class="flex cursor-pointer items-center gap-12px"
					>
						<Checkbox
							:checked="filterState.itemCondition.includes(option.id)"
							:value="option.id"
							@update:checked="
								(checked: boolean) =>
									handleCheckboxChange('itemCondition', option.id, checked)
							"
							class="h-14px w-14px"
						/>
						<span class="typography-s1-reg text-gray-700">
							{{ option.label }}
						</span>
					</label>
				</div>
			</div>

			<div>
				<h4 class="typography-b3-reg mb-16px">
					{{ filterContent.status.title }}
				</h4>
				<div class="flex flex-col gap-16px">
					<label
						v-for="option in filterContent.status.options"
						:key="option.id"
						class="flex cursor-pointer items-center gap-12px"
					>
						<Checkbox
							:checked="filterState.status.includes(option.id)"
							:value="option.id"
							@update:checked="
								(checked: boolean) =>
									handleCheckboxChange('status', option.id, checked)
							"
							class="h-14px w-14px"
						/>
						<span class="typography-s1-reg text-gray-700">
							{{ option.label }}
						</span>
					</label>
				</div>
			</div>

			<div>
				<h4 class="typography-b3-reg mb-16px">
					{{ filterContent.price.title }}
				</h4>
				<div class="flex flex-col gap-16px">
					<input
						type="text"
						:value="filterState.price.min"
						@input="(e: Event) => handlePriceChange('min', e)"
						:placeholder="filterContent.price.options[0].label"
						inputmode="numeric"
						pattern="[0-9]*"
						class="typography-s1-reg w-full rounded-lg border border-n-300 px-16px py-[10px] outline-none"
						:class="{ 'border-r-700': !isMinPriceValid || !isPriceRangeValid }"
					/>
					<input
						type="text"
						:value="filterState.price.max"
						@input="(e: Event) => handlePriceChange('max', e)"
						:placeholder="filterContent.price.options[1].label"
						inputmode="numeric"
						pattern="[0-9]*"
						class="typography-s1-reg w-full rounded-lg border border-n-300 px-16px py-[10px] outline-none"
						:class="{ 'border-r-700': !isMaxPriceValid || !isPriceRangeValid }"
					/>
					<div
						class="typography-c2-reg mt-4px text-n-200"
						:class="{ 'text-r-700': hasPriceError }"
					>
						{{ filterContent.price.description }}
					</div>
				</div>
			</div>

			<div>
				<h4 class="typography-b3-reg mb-16px">
					{{ filterContent.marketplace.title }}
				</h4>
				<div class="flex flex-col gap-16px">
					<label
						v-for="option in filterContent.marketplace.options"
						:key="option.id"
						class="flex cursor-pointer items-center gap-12px"
					>
						<Checkbox
							:checked="filterState.marketplace.includes(option.id)"
							:value="option.id"
							@update:checked="
								(checked: boolean) =>
									handleCheckboxChange('marketplace', option.id, checked)
							"
							class="h-14px w-14px"
						/>
						<span class="typography-s1-reg text-gray-700">
							{{ option.label }}
						</span>
					</label>
				</div>
			</div>
		</div>
	</div>
</template>
